import { GetterTree } from "vuex/types";
import { RootState } from "@/store/types";
import { FileUploadState } from "./types";

/**
 * Getters for the File Storage module.
 * Provides computed access to storage type and backend checks.
 */
const getters: GetterTree<FileUploadState, RootState> = {
  /**
   * Returns the current storage backend type.
   *
   * @param {FileUploadState} state - Module state
   * @returns {string} Storage type ('azure' or 'ceph')
   */
  storageType(state, getters, rootState) {
    return state.storageType;
  },

  /**
   * Checks if Azure storage is configured.
   *
   * @param {FileUploadState} state - Module state
   * @returns {boolean} True if using Azure storage
   */
  isAzureStorage(state, getters, rootState) {
    return state.storageType === "azure";
  },

  /**
   * Checks if Ceph storage is configured.
   *
   * @param {FileUploadState} state - Module state
   * @returns {boolean} True if using Ceph storage
   */
  isCephStorage(state, getters, rootState) {
    return state.storageType === "ceph";
  },

  /**
   * Returns the maximum file upload size in MB, sourced from the server env.
   *
   * @param {FileUploadState} state - Module state
   * @returns {number} Maximum upload size in MB
   */
  fileUploadSizeLimit(state, getters, rootState) {
    return state.fileUploadSizeLimit;
  },

  /**
   * Deployment upload-size ceiling in MB, without the per-bot Core layer.
   * The Core module editor uses this as its `max` so the form's ceiling does
   * not drift with whatever value was last saved.
   *
   * @param {FileUploadState} state - Module state
   * @returns {number} Ceiling in MB
   */
  fileUploadSizeCeiling(state) {
    return state.fileUploadSizeCeiling;
  },

  /**
   * Returns the server-provided allowed file extensions.
   * Falls back to an empty array when the server returns null/undefined.
   *
   * @param {FileUploadState} state - Module state
   * @returns {string[]} Allowed extensions (e.g. [".pdf", ".docx"])
   */
  fileUploadAllowedExtensions(state) {
    return state.fileUploadAllowedExtensions ?? [];
  },

  /**
   * Returns the server-provided allowed MIME types.
   * Falls back to an empty array when the server returns null/undefined.
   *
   * @param {FileUploadState} state - Module state
   * @returns {string[]} Allowed MIME types (e.g. ["application/pdf"])
   */
  fileUploadAllowedMimeTypes(state) {
    return state.fileUploadAllowedMimeTypes ?? [];
  },
};

export default getters;
